import LoadingScreen from 'components/LoadingScreen';
import PageLayout from 'components/PageLayout';
import ActiveFiltersBar from 'components/SearchAndFilters/SelectedFiltersBar';
import { useAtomValue, useSetAtom } from 'jotai';
import { Suspense, useLayoutEffect, useRef } from 'react';
import { CardTitle, Col, Row } from 'reactstrap';

import AssetInsights from './AssetInsights';
import AssetListingDataGrid from './AssetListingDataGrid';
import BulkActions from './BulkActions';
import Filters from './Filters';
import { useFilterConfig } from './Filters.config';
import {
  assetDataAtom,
  componentHeightAtom,
  filterValuesAtom,
  selectedRowIdsAtom,
  useOnFilterChangeResetRelatedAtoms,
} from './state';
import { appliedHostIdsAtom } from '../state';

const SelectedFilters: React.FC<{ filtersConfig }> = ({ filtersConfig }) => {
  const setFilterValues = useSetAtom(filterValuesAtom);
  const resetOnFilterChangeAtoms = useOnFilterChangeResetRelatedAtoms();

  return (
    <Suspense fallback={<></>}>
      <ActiveFiltersBar
        filtersConfig={filtersConfig}
        onFiltersReset={() => {
          setFilterValues({ ...filtersConfig.getActiveFilterValues() });
          resetOnFilterChangeAtoms();
        }}
      />
    </Suspense>
  );
};

const ListingCount: React.FC = () => {
  const listingData = useAtomValue(assetDataAtom);
  return <>{listingData?.loading ? '...' : listingData.count}</>;
};

const AssetListing: React.FC = () => {
  const filtersConfig = useFilterConfig();

  const selectedRowIds = useAtomValue(selectedRowIdsAtom);
  const appliedRowIds = useAtomValue(appliedHostIdsAtom);
  const setComponentHeight = useSetAtom(componentHeightAtom);

  const targetRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (targetRef.current) {
      setComponentHeight(targetRef.current.offsetHeight);
    }
  }, []);
  return (
    <div ref={targetRef}>
      <PageLayout
        id="sast_repos_listing"
        heading={
          <Row>
            <Col md="auto" sm="12">
              <CardTitle>
                <h4>
                  Host Assets
                  <label id="counts" className="label btn">
                    <Suspense fallback={<>...</>}>
                      <ListingCount />
                    </Suspense>
                  </label>
                </h4>
              </CardTitle>
            </Col>
            {appliedRowIds?.length > 0 && (
              <Col md="3" sm="12" className="text-left text-nowrap">
                <>
                  Applied:{' '}
                  <label
                    className="label btn"
                    style={{
                      backgroundColor: 'rgba(7, 114, 219, 0.293)',
                      color: 'black',
                    }}
                  >
                    {appliedRowIds.length}
                  </label>
                </>
              </Col>
            )}
          </Row>
        }
        subheading="List of Host Assets"
        filteringOptions={
          <Suspense fallback={<>...</>}>
            <Filters filtersConfig={filtersConfig} />
          </Suspense>
        }
      >
        <Suspense fallback={<LoadingScreen />}>
          <div className="container-fluid p-0">
            <div className="row mb-1">
              {/* Expanding Column */}
              <div className="col">
                <SelectedFilters filtersConfig={filtersConfig} />
              </div>

              {/* Fixed Column with Right-Aligned Content */}
              <div className="col-auto text-right align-baseline">
                <Suspense fallback={<>...</>}>
                  <BulkActions />
                </Suspense>
              </div>
            </div>
            {/* Add the summary component here */}
            <Suspense fallback={<LoadingScreen />}>
              <AssetInsights />
            </Suspense>
            <Suspense fallback={<LoadingScreen />}>
              <AssetListingDataGrid />
            </Suspense>
          </div>
        </Suspense>
      </PageLayout>
    </div>
  );
};

const AssetsList = () => {
  const componentHeight = useAtomValue(componentHeightAtom);

  return (
    <Suspense fallback={<LoadingScreen height={componentHeight} />}>
      <AssetListing />
    </Suspense>
  );
};

export default AssetsList;
